/**
 * QorTrace — Language suggestion toast (Iter249)
 *
 * Shown ONCE per browser when the detected/preferred language differs from
 * the currently active one. Never auto-switches; just nudges. Dismissal is
 * persisted in localStorage so we don't nag.
 *
 * Detection priority (matches i18n.js):
 *   1. Browser Accept-Language preference (navigator.languages[0])
 *   2. Server GeoIP hint from /api/public/lang-hint (Day 2 enhancement)
 *
 * Hidden if:
 *   - Active lang already matches preference
 *   - User dismissed the toast before
 *   - Active lang was explicitly set via cookie/URL (no nag)
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { X, Languages } from "lucide-react";
import { SUPPORTED_LANGUAGES, SUPPORTED_LANGUAGE_CODES } from "../i18n";

const STORAGE_KEY = "qt_lang_suggestion_dismissed";
const API = process.env.REACT_APP_BACKEND_URL;

function getBrowserPreference() {
  // navigator.languages is the ordered list of user-preferred languages
  const list = navigator.languages || [navigator.language || "en"];
  for (const raw of list) {
    const code = (raw || "").split("-")[0].toLowerCase();
    if (SUPPORTED_LANGUAGE_CODES.includes(code)) return code;
  }
  return null;
}

export default function LanguageSuggestionToast() {
  const { i18n, t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [suggested, setSuggested] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Already dismissed?
      try {
        if (localStorage.getItem(STORAGE_KEY)) return;
      } catch (_) { /* private mode */ }

      // Wait one tick for i18n detection to settle.
      await new Promise((r) => setTimeout(r, 800));
      const active = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];

      // If the user has an explicit cookie pick, never nag.
      try {
        if (document.cookie.includes("qt_lang=")) return;
      } catch (_) { /* no-op */ }

      // Look at browser preference first.
      let preferred = getBrowserPreference();

      // If browser is English but we can still find a GeoIP hint, try that.
      if (!preferred || preferred === "en") {
        try {
          const r = await axios.get(`${API}/api/public/lang-hint`, { timeout: 4000 });
          const hint = (r.data?.suggested_lang || "").toLowerCase();
          if (hint && SUPPORTED_LANGUAGE_CODES.includes(hint)) {
            preferred = hint;
          }
        } catch (_) { /* offline / GeoIP unavailable — silently skip */ }
      }

      if (!preferred || preferred === active) return;
      if (cancelled) return;

      setSuggested(preferred);
      setVisible(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) { /* no-op */ }
  };

  const accept = () => {
    if (!suggested) return;
    i18n.changeLanguage(suggested);
    document.documentElement.setAttribute("lang", suggested);
    // Iter250 — match LanguageSwitcher: rewrite URL to the locale prefix
    // so the session is consistent with /de/, /fr/, ... canonical routing.
    try {
      const { pathname, search, hash } = window.location;
      const segments = pathname.split("/").filter(Boolean);
      const head = (segments[0] || "").toLowerCase();
      const tail = SUPPORTED_LANGUAGE_CODES.includes(head)
        ? "/" + segments.slice(1).join("/")
        : pathname;
      const cleanTail = tail === "/" || tail === "" ? "/" : tail.startsWith("/") ? tail : `/${tail}`;
      const nextPath = suggested === "en" ? cleanTail : `/${suggested}${cleanTail === "/" ? "" : cleanTail}`;
      dismiss();
      window.location.assign(nextPath + search + hash);
      return;
    } catch (_) { /* fall through */ }
    dismiss();
  };

  if (!visible || !suggested) return null;

  const langMeta = SUPPORTED_LANGUAGES.find((l) => l.code === suggested);
  if (!langMeta) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] max-w-md w-[calc(100%-2rem)] rounded-xl border border-cyan-500/40 bg-zinc-950/95 backdrop-blur p-4 shadow-2xl"
      role="dialog"
      aria-live="polite"
      data-testid="lang-suggestion-toast"
    >
      <div className="flex items-start gap-3">
        <Languages className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white">
            {t("language.suggestion_title", { language: langMeta.native })}
          </div>
          <div className="text-xs text-zinc-400 mt-1 leading-relaxed">
            {t("language.suggestion_body", { language: langMeta.native })}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={accept}
              className="px-3 py-1.5 rounded-md bg-cyan-500 text-zinc-950 text-xs font-medium hover:bg-cyan-400 transition flex items-center gap-1.5"
              data-testid="lang-suggestion-accept"
            >
              <span className="text-base leading-none">{langMeta.flag}</span>
              {t("language.suggestion_yes", { language: langMeta.native })}
            </button>
            <button
              onClick={dismiss}
              className="px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-400 hover:text-zinc-200 text-xs"
              data-testid="lang-suggestion-dismiss"
            >
              {t("language.suggestion_dismiss")}
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="flex-shrink-0 text-zinc-500 hover:text-zinc-300"
          aria-label="Dismiss"
          data-testid="lang-suggestion-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
